import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { IRiskTemplateResponse } from "@gloria-public/risk/domain/interfaces/risk.interface";
import { RiskService } from "@gloria-public/risk/domain/services/risk.service";
import { TranslateService } from "@ngx-translate/core";
import { BackendTableDataSource } from "@shared/utils/backend-table.datasource";
import { Subject, combineLatest, distinctUntilChanged, of, startWith, switchMap, takeUntil } from "rxjs";

@Component({
  selector: "app-landing-page",
  templateUrl: "./landing-page.component.html",
  styleUrls: ["./landing-page.component.scss"],
})
export class LandingPageComponent implements OnInit, OnDestroy {
  public globalDnaDataSource: BackendTableDataSource<IRiskTemplateResponse>; // Datasource for the featured DNA risk (first result tagged "DNA")
  public differentRisksDataSource: BackendTableDataSource<IRiskTemplateResponse>; // Datasource for the 5 most recent non-DNA risks
  public globalDNA?: IRiskTemplateResponse;
  public differentRisks: IRiskTemplateResponse[] = [];

  /** Emits when the component is destroyed, used to complete all active subscriptions */
  private onDestroy$ = new Subject<void>();

  constructor(
    private riskService: RiskService,
    private translateService: TranslateService,
    private router: Router,
    public snackBar: MatSnackBar
  ) {
    // Fetch the single featured DNA risk
    this.globalDnaDataSource = new BackendTableDataSource(
      (p) =>
        combineLatest([
          of(p),
          this.translateService.onLangChange.pipe(
            startWith({ lang: this.translateService.currentLang }),
            distinctUntilChanged((a, b) => a.lang === b.lang),
            takeUntil(this.onDestroy$)
          ),
        ]).pipe(
          switchMap(([params, event]) => this.riskService.getRisks({ ...params, lang: event.lang, tags: ["DNA"] })),
          takeUntil(this.onDestroy$)
        ),
      () => this.riskService.getRisksCount({ tags: ["DNA"] })
    );

    // Fetch the 5 most recent risks, sorted by date descending, excluding DNA tag
    this.differentRisksDataSource = new BackendTableDataSource(
      (p) =>
        combineLatest([
          of(p),
          this.translateService.onLangChange.pipe(
            startWith({ lang: this.translateService.currentLang }),
            distinctUntilChanged((a, b) => a.lang === b.lang),
            takeUntil(this.onDestroy$)
          ),
        ]).pipe(
          switchMap(([params, event]) => this.riskService.getRisks({ ...params, lang: event.lang, sortByDate: "DESCENDING" })),
          takeUntil(this.onDestroy$)
        ),
      () => this.riskService.getRisksCount()
    );
  }

  /**
   * loads the Datasources.
   */
  ngOnInit(): void {
    this.globalDnaDataSource.loadData({ page: 1, pageSize: 1 });
    this.differentRisksDataSource.loadData({ page: 1, pageSize: 5 });

    this.globalDnaDataSource
      .connect()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((globalDNA) => {
        this.globalDNA = globalDNA[0];
      });
    this.differentRisksDataSource
      .connect()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((risks) => {
        this.differentRisks = risks.filter((r) => !r.tags.includes("DNA"));
      });
  }

  /**
   * Cleans up subscriptions and subjects when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Navigates to risk details page
   * @param id risk identifier
   */
  public goToRisk(id: string): void {
    this.router.navigate(["/risks", id]);
  }
}
